import { useEffect, useState } from 'react';
import { Duck } from '../components/Duck';
import type { MascotName } from '../theme/ThemeProvider';

const EVENT_DATE = new Date('2026-05-29T10:30:00-07:00');

type Props = {
  mascot: MascotName;
  registrations: number;
};

export function Hero({ mascot, registrations }: Props): JSX.Element {
  return (
    <section className="relative overflow-hidden bg-bg px-8 pt-16 pb-20">
      <FloatingDucks mascot={mascot} />
      <div className="relative mx-auto max-w-[1280px]">
        <div className="grid items-center gap-12" style={{ gridTemplateColumns: '1.7fr 1fr' }}>
          <div className="min-w-0">
            <div
              className="mb-6 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-duck px-3 py-1.5 font-mono text-xs font-bold text-ink"
              style={{ boxShadow: '3px 3px 0 rgb(var(--color-ink))' }}
            >
              <span className="h-2 w-2 rounded-full bg-beak motion-safe:animate-pulse" />
              EPISODE 01 · SEMI-LIVE · MAY 29
            </div>

            <h1
              className="m-0 font-display font-black tracking-[-0.04em] text-ink"
              style={{ fontSize: 'clamp(40px, 5.4vw, 84px)', lineHeight: 0.96, textWrap: 'balance' as never, overflowWrap: 'break-word' }}
            >
              The{' '}
              <span
                className="bg-duck px-[0.08em]"
                style={{ boxShadow: '3px 3px 0 rgb(var(--color-ink))', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' as never }}
              >
                Rubber Duck
              </span>{' '}
              Debate.
            </h1>

            <p className="mt-7 max-w-[600px] text-[22px] leading-snug text-ink-soft">
              Semi-live debates where humans and AI bots argue, equally, about whether AI is actually any good at
              engineering. <strong className="text-ink">The bots get to talk back.</strong>
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href="#register"
                className="rounded-full bg-ink px-7 py-[18px] font-display text-lg font-extrabold text-bg no-underline"
                style={{ boxShadow: '5px 5px 0 rgb(var(--color-beak))' }}
              >
                Reserve a seat — free
              </a>
              <a
                href="#chat-teaser"
                className="rounded-full border-2 border-ink bg-paper px-[26px] py-4 font-display text-lg font-bold text-ink no-underline"
              >
                💬 Argue with a bot panelist
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4 font-mono text-[13px] text-ink-soft">
              <span>{registrations.toLocaleString()} ducks in the bath</span>
              <span className="h-4 w-px bg-[rgb(var(--color-ink)/12%)]" />
              <span>5 humans + 2 bots arguing</span>
              <span className="h-4 w-px bg-[rgb(var(--color-ink)/12%)]" />
              <span>BYO rubber duck</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-5">
            <Countdown />
            <div
              className="w-full max-w-[360px] rounded-2xl border-2 border-ink bg-paper px-6 py-5"
              style={{ boxShadow: '5px 5px 0 rgb(var(--color-ink))' }}
            >
              <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.12em] text-ink-soft">
                THIS EPISODE'S MOTION
              </div>
              <div className="font-display text-[22px] font-bold leading-tight text-ink">
                "Let's Get Real: Agentic Software Engineering, is it&nbsp;working?"
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {['#agents', '#evals', '#review-ux', '#vibes'].map((t) => (
                  <span
                    key={t}
                    className="rounded-md border border-[rgb(var(--color-ink)/12%)] bg-bg px-2 py-1 font-mono text-[11px] text-ink-soft"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes float-slow { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-12px) rotate(3deg); } }
      `}</style>
    </section>
  );
}

function FloatingDucks({ mascot }: { mascot: MascotName }): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute top-10 opacity-20" style={{ right: '8%', animation: 'float-slow 6s ease-in-out infinite' }}>
        <Duck variant={mascot} size={120} />
      </div>
      <div className="absolute bottom-16 opacity-15" style={{ left: '4%', animation: 'float-slow 8s ease-in-out infinite', animationDelay: '1.5s' }}>
        <Duck variant={mascot} size={90} />
      </div>
      <div className="absolute opacity-10" style={{ top: '35%', left: '38%', animation: 'float-slow 7s ease-in-out infinite', animationDelay: '3s' }}>
        <Duck variant={mascot} size={70} />
      </div>
    </div>
  );
}

function Countdown(): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, EVENT_DATE.getTime() - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  const units = [
    { n: days, l: 'days' },
    { n: hours, l: 'hrs' },
    { n: mins, l: 'min' },
    { n: secs, l: 'sec' },
  ];
  return (
    <div
      className="w-full max-w-[360px] rounded-2xl bg-ink px-6 py-5"
      style={{ boxShadow: '5px 5px 0 rgb(var(--color-duck))' }}
    >
      <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-duck">◉ DEBATE STARTS IN</div>
      <div className="grid grid-cols-4 gap-2">
        {units.map((u) => (
          <div key={u.l} className="rounded-lg bg-bg px-1 py-2.5 text-center">
            <div className="font-display text-[32px] font-extrabold leading-none text-ink tabular-nums">
              {String(u.n).padStart(2, '0')}
            </div>
            <div className="mt-0.5 font-mono text-[10px] uppercase text-ink-soft">{u.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 font-mono text-[11px] text-duck opacity-70">May 29, 2026 · 10:30 AM PST · Streamed to your nearest tub</div>
    </div>
  );
}
